
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "updating-a-job-ad"
    }}>{`Updating a job ad`}</h1>
    <p>{`Job ads can be modified under their existing URL after they’ve been posted.
While most fields can be freely updated, there are some restrictions:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Job ads cannot be moved between `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/position-openings"
          }}>{`position openings`}</a>{` or SEEK hirers.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Updates to `}<inlineCode parentName="p">{`positionTitle`}</inlineCode>{`, `}<inlineCode parentName="p">{`jobCategories`}</inlineCode>{`, `}<inlineCode parentName="p">{`positionLocation`}</inlineCode>{` and `}<inlineCode parentName="p">{`postingInstructions.seekAdvertisementProductId`}</inlineCode>{` may result in an additional charge for that job ad.`}</p>
        <p parentName="li">{`See `}<a parentName="p" {...{
            "href": "https://help.seek.com.au/employer/s/article/Variable-Pricing-How-does-variable-pricing-work"
          }}>{`variable pricing`}</a>{` to learn more.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Providing a `}<inlineCode parentName="p">{`seekApplicationQuestionnaireId`}</inlineCode>{` field to the `}<a parentName="p" {...{
            "href": "#updatepostedpositionprofile"
          }}><inlineCode parentName="a">{`updatePostedPositionProfile`}</inlineCode></a>{` mutation will fail with a `}<a parentName="p" {...{
            "href": "/graphql/error-responses#bad_user_input"
          }}><inlineCode parentName="a">{`BAD_USER_INPUT`}</inlineCode>{` error`}</a>{`.`}</p>
        <p parentName="li">{`See the `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/questionnaires#immutability"
          }}>{`questionnaire immutability`}</a>{` documentation for more information.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`We do not recommend changing the `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/application-methods"
          }}>{`application method`}</a>{` of a job ad while it is live on the SEEK platform.`}</p>
        <p parentName="li">{`Attempting to switch a live job ad between SEEK’s Apply Form and an external apply form may result in unexpected behaviour and complicate applicant tracking.
You may support closing and re-posting the job ad to SEEK with a different `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/application-methods"
          }}>{`application method`}</a>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`You cannot provide the same `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/UpdatePostingInstructionInput/field/seekAdvertisementProductId"
          }}><inlineCode parentName="a">{`seekAdvertisementProductId`}</inlineCode></a>{` that was initially used to post a job ad.`}</p>
        <p parentName="li">{`Display `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/ad-selection"
          }}>{`ad selection`}</a>{` to the user in your update workflow.
This will present a distinct set of update products along with associated pricing and payment information.
Once an update product has been selected, its ID can be used to update the job ad:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-jsonc"
          }}>{`{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      // ...
      "postingInstructions": {
        // Provide updated product ID if ad selection was displayed
        "seekAdvertisementProductId": "globalPublicTest:adProduct:seekApi:GxWYyP4HtLsXHEVMJFcPoRw1hs6WoAakfu13TRkq21vQ"
        // ...
      }
    }
  }
}
`}</code></pre>
        <p parentName="li">{`In certain scenarios,
you may need to update a job ad without displaying ad selection to the hirer.
You can retain the existing ad product by `}<a parentName="p" {...{
            "href": "/graphql/in-practice#optional-arguments"
          }}>{`omitting`}</a>{` the `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/UpdatePostingInstructionInput/field/seekAdvertisementProductId"
          }}><inlineCode parentName="a">{`seekAdvertisementProductId`}</inlineCode></a>{` field entirely:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-jsonc"
          }}>{`{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      // ...
      "postingInstructions": {
        // Omit \`seekAdvertisementProductId\` if ad selection was not displayed
        // ...
      }
    }
  }
}
`}</code></pre>
      </li>
    </ul>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <h3 {...{
      "id": "updatepostedpositionprofile"
    }}>{`updatePostedPositionProfile`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/mutation/updatePostedPositionProfile"
      }}><inlineCode parentName="a">{`updatePostedPositionProfile`}</inlineCode>{` mutation`}</a>{` updates a live job ad.
The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{`’s existing fields will be replaced with the provided input fields.
You must provide the current values of any fields you don’t want to modify.
An exception is made for `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/UpdatePostingInstructionInput/field/end"
      }}><inlineCode parentName="a">{`UpdatePostingInstructionInput.end`}</inlineCode></a>{`,
where omitting the field will preserve the existing end date.`}</p>
    <p>{`For example, you could run this mutation to update a job ad with a new title:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: UpdatePostedPositionProfileInput!) {\\n  updatePostedPositionProfile(input: $input) {\\n    positionProfile {\\n      profileId {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":3113},\"end\":{\"line\":89,\"column\":4,\"offset\":3303},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionProfile\\\": {\\n      \\\"profileId\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\",\\n      // Update the `positionTitle` from `Principal Developer` to `Lead Developer`\\n      \\\"positionTitle\\\": \\\"Lead Developer\\\",\\n      \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n      \\\"positionFormattedDescriptions\\\": [\\n        {\\n          \\\"descriptionId\\\": \\\"SearchSummary\\\",\\n          \\\"content\\\": \\\"Experienced software developer wanted to lead an existing team.\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Line management required\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Need experience with cloud deployments\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Competitive benefits packages\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n          \\\"content\\\": \\\"<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>\\\"\\n        }\\n      ],\\n      \\\"offeredRemunerationPackage\\\": {\\n        \\\"basisCode\\\": \\\"Salaried\\\",\\n        \\\"ranges\\\": {\\n          \\\"minimumAmount\\\": {\\n            \\\"value\\\": 100000,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"maximumAmount\\\": {\\n            \\\"value\\\": 130000,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"intervalCode\\\": \\\"Year\\\"\\n        },\\n        \\\"descriptions\\\": [\\\"Up to $130k excluding super\\\"]\\n      },\\n      \\\"seekAnzWorkTypeCode\\\": \\\"FullTime\\\",\\n      \\\"seekVideo\\\": {\\n        \\\"url\\\": \\\"https://www.youtube.com/embed/Q3UvigPHCjI\\\",\\n        \\\"seekAnzPositionCode\\\": \\\"Above\\\"\\n      },\\n      \\\"jobCategories\\\": \\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n      \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n      \\\"postingInstructions\\\": {\\n        \\\"seekAdvertisementProductId\\\": \\\"globalPublicTest:adProduct:seekApi:GxWYyP4HtLsXHEVMJFcPoRw1hs6WoAakfu13TRkq21vQ\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":91,\"column\":1,\"offset\":3305},\"end\":{\"line\":149,\"column\":4,\"offset\":5400},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"updatePostedPositionProfile\\\": {\\n    \\\"positionProfile\\\": {\\n      \\\"profileId\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":5402},\"end\":{\"line\":161,\"column\":4,\"offset\":5585},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "UpdatePostedPositionProfileInput!)": true,
        "{\\n": true,
        "": true,
        "updatePostedPositionProfile(input:": true,
        "$input)": true,
        "positionProfile": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":3113},\"end\":{\"line\":89,\"column\":4,\"offset\":3303},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"positionProfile\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\",\\n": true,
        "//": true,
        "Update": true,
        "the": true,
        "`positionTitle`": true,
        "from": true,
        "`Principal": true,
        "Developer`": true,
        "to": true,
        "`Lead": true,
        "Developer`\\n": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Lead": true,
        "Developer\\\",\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "[\\n": true,
        "\\\"descriptionId\\\":": true,
        "\\\"SearchSummary\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"Experienced": true,
        "software": true,
        "developer": true,
        "wanted": true,
        "lead": true,
        "an": true,
        "existing": true,
        "team.\\\"\\n": true,
        "},\\n": true,
        "\\\"SearchBulletPoint\\\",\\n": true,
        "\\\"Line": true,
        "management": true,
        "required\\\"\\n": true,
        "\\\"Need": true,
        "experience": true,
        "with": true,
        "cloud": true,
        "deployments\\\"\\n": true,
        "\\\"Competitive": true,
        "benefits": true,
        "packages\\\"\\n": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"<p>Acme": true,
        "Inc": true,
        "is": true,
        "looking": true,
        "for": true,
        "experienced": true,
        "their": true,
        "transformation.</p><p>You": true,
        "should": true,
        "bring": true,
        "your": true,
        "passion": true,
        "both": true,
        "technology": true,
        "and": true,
        "people": true,
        "this": true,
        "exciting": true,
        "role.</p>\\\"\\n": true,
        "],\\n": true,
        "\\\"offeredRemunerationPackage\\\":": true,
        "\\\"basisCode\\\":": true,
        "\\\"Salaried\\\",\\n": true,
        "\\\"ranges\\\":": true,
        "\\\"minimumAmount\\\":": true,
        "\\\"value\\\":": true,
        "100000,\\n": true,
        "\\\"currency\\\":": true,
        "\\\"AUD\\\"\\n": true,
        "\\\"maximumAmount\\\":": true,
        "130000,\\n": true,
        "\\\"intervalCode\\\":": true,
        "\\\"Year\\\"\\n": true,
        "\\\"descriptions\\\":": true,
        "[\\\"Up": true,
        "$130k": true,
        "excluding": true,
        "super\\\"]\\n": true,
        "\\\"seekAnzWorkTypeCode\\\":": true,
        "\\\"FullTime\\\",\\n": true,
        "\\\"seekVideo\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://www.youtube.com/embed/Q3UvigPHCjI\\\",\\n": true,
        "\\\"seekAnzPositionCode\\\":": true,
        "\\\"Above\\\"\\n": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n": true,
        "\\\"postingInstructions\\\":": true,
        "\\\"seekAdvertisementProductId\\\":": true,
        "\\\"globalPublicTest:adProduct:seekApi:GxWYyP4HtLsXHEVMJFcPoRw1hs6WoAakfu13TRkq21vQ\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":91,\"column\":1,\"offset\":3305},\"end\":{\"line\":149,\"column\":4,\"offset\":5400},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"updatePostedPositionProfile\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2782PZfXV\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":5402},\"end\":{\"line\":161,\"column\":4,\"offset\":5585},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: UpdatePostedPositionProfileInput!) {
  updatePostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
`}</code></pre>
    <p>{`You will receive a `}<a parentName="p" {...{
        "href": "/graphql/error-responses#forbidden"
      }}><inlineCode parentName="a">{`FORBIDDEN`}</inlineCode>{` error`}</a>{` if you try to update a job ad that has already been `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/closing-a-job-ad"
      }}>{`closed`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;